export type TranslationValue =
  | string
  | TranslationTree
  | ReadonlyArray<string | Record<string, string>>
  | Array<string | Record<string, string>>;

/** Structural shape for translation trees (open index for nesting). */
export type TranslationTree = { [key: string]: TranslationValue };

/** Dashboard chrome languages: Danish, English, Swedish. */
export type DashboardTranslations = {
  da: TranslationTree;
  en: TranslationTree;
  sv: TranslationTree;
};

/**
 * Identity helper that checks a tree against {@link TranslationTree} while
 * preserving literal key types (unlike `: TranslationTree` annotations).
 */
export function defineTranslationTree<const T extends TranslationTree>(tree: T): T {
  return tree;
}

/**
 * Same as {@link defineTranslationTree} for `{ da, en, sv }` bundles so
 * `keyof typeof bundle.da` stays concrete.
 */
export function defineDashboardTranslations<
  const Da extends TranslationTree,
  const En extends TranslationTree,
  const Sv extends TranslationTree,
>(bundle: { da: Da; en: En; sv: Sv }): { da: Da; en: En; sv: Sv } {
  return bundle;
}

/** Resolve a flat translation key to a string (falls back to the key). */
export function resolveTranslationString(tree: TranslationTree, key: string): string {
  const value = tree[key];
  return typeof value === "string" ? value : key;
}

/** `key` from the bundle's `language` tree, else its English one, else the key itself. */
export function translateDashboardKey(
  bundle: Partial<Record<string, TranslationTree>>,
  language: string,
  key: string,
): string {
  const value = bundle[language]?.[key] ?? bundle["en"]?.[key];
  return typeof value === "string" ? value : key;
}
